import React, { useEffect, useMemo, useRef, useState } from "react"
import { Flex, Form, Input, InputRef, Popover, theme, Typography } from "antd"
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons"

type Props = {
  extra?: string
  formLabel?: string
  formName?: string
}

export const RegistrationPasswordField = ({
  extra,
  formLabel = "Password",
  formName = "password",
}: Props) => {
  const form = Form.useFormInstance()
  const value = Form.useWatch("password", form)
  const { token } = theme.useToken()
  const triggerRef = useRef<InputRef>(null)
  const [triggerWidth, setTriggerWidth] = useState(0)

  useEffect(() => {
    if (triggerRef.current) {
      const inputWidth = triggerRef.current.nativeElement?.offsetWidth as number
      const paddingSize = 44
      setTriggerWidth(inputWidth + paddingSize)
    }
  }, [])

  const validationTips = useMemo(() => {
    const tips = [
      {
        check: () => value && value.length >= 8,
        text: "Content at least 8 characters",
      },
      {
        check: () => value && new RegExp(/[a-z]/).test(value),
        text: "Content at least one lowercase letter",
      },
      {
        check: () => value && new RegExp(/[A-Z]/).test(value),
        text: "Content at least one uppercase letter",
      },
      {
        check: () =>
          new RegExp(/[!@#$%^&*(),.?":{}|<>_\-\[\]\\\/`~]/).test(value),
        text: "Content at least one special characters",
      },
      {
        check: () => new RegExp(/\d/).test(value),
        text: "Content at least one number characters",
      },
    ]

    return (
      <Flex gap={12} style={{ width: "100%" }} vertical>
        {tips.map((tip) => {
          const isValid = tip.check()
          return (
            <Flex align="center" gap={8} key={tip.text}>
              {isValid ? (
                <CheckCircleFilled style={{ color: token.colorPrimary }} />
              ) : (
                <CloseCircleFilled style={{ color: token.colorError }} />
              )}

              <Typography.Text>{tip.text}</Typography.Text>
            </Flex>
          )
        })}
      </Flex>
    )
  }, [value])

  return (
    <Popover
      content={validationTips}
      overlayStyle={{ width: triggerWidth }}
      placement="bottom"
      trigger="focus"
    >
      <Form.Item
        extra={extra}
        hasFeedback
        id="GENERATED_ID_REGISTRATIONPASSWORDFIELD_TSX_FORM.ITEM_VOID_1"
        label={formLabel}
        name={formName}
        rules={[
          {
            message: "Please input your password",
            required: true,
          },
          {
            message: "The length must be no less than 8 characters",
            min: 8,
          },
        ]}
      >
        <Input.Password
          placeholder="Enter your password"
          ref={triggerRef as any}
        />
      </Form.Item>
    </Popover>
  )
}
